import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import moment from "moment";
import { 
  Row, 
  Col, 
  Card, 
  Tag, 
  Table, 
  Typography, 
  Space,
  Avatar,
  Statistic,
  Input,
  Badge,
  List
} from 'antd';
import {
  UserOutlined,
  BookOutlined,
  FieldTimeOutlined,
  TeamOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  HourglassOutlined,
  BarChartOutlined
} from '@ant-design/icons';
import withRoleAccess from '../../hoc/withRoleAccess';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import MonthlyHoursTrends from '../../components/MonthlyHoursTrends';
import { useTranslation } from 'react-i18next';

const { Title, Text } = Typography;

function TeacherDashboardAdmin() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [teacher, setTeacher] = useState(null);
  const [classGroups, setClassGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [registrationsCount, setRegistrationsCount] = useState([]);
  const [todaysClasses, setTodaysClasses] = useState([]);
  const [totalMonthlyHours, setTotalMonthlyHours] = useState(0);
  const [newClassSearchText, setNewClassSearchText] = useState('');
const [allClassSearchText, setAllClassSearchText] = useState('');
  const API_URL = process.env.REACT_APP_API_URL;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { day: '2-digit', month: 'short', year: 'numeric' };
    return new Intl.DateTimeFormat(i18n.language, options).format(date).toUpperCase();
  };

  const formatDateTime = (dateTimeString) => {
    const date = new Date(dateTimeString);
    const options = {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Intl.DateTimeFormat(i18n.language, options).format(date).toUpperCase();
  };

  const formatMinutesToHours = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}:${remainingMinutes.toString().padStart(2, '0')} ${t('hr')}`;
  };
  const formatScheduleHour = (scheduleHourString) => {
    try {
      const [hours, minutes] = scheduleHourString.split(' ').map((value) => parseInt(value, 10));
      return `${hours}:${minutes.toString().padStart(2, '0')} ${t('hr')}`;
    } catch (error) {
      console.error('Invalid schedule hour value:', scheduleHourString);
      return 'Invalid Time';
    }
  };

const formatTimeHHMM = (timeString) => {
    if (!timeString) return 'N/A';
    try {
      // Handle different time formats
      if (timeString.includes(':')) {
        const [hours, minutes] = timeString.split(':');
        return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
      }
      return timeString;
    } catch (error) {
      console.error('Error formatting time:', timeString, error);
      return 'N/A';
    }
  };

   const fetchTeacherClasses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/classes/teacher/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const today = moment().format('YYYY-MM-DD');
      const todayClasses = response.data.filter(cls => {
        const classDate = moment(cls.date).format('YYYY-MM-DD');
        return classDate === today;
      }).sort((a, b) => new Date(`${moment(a.date).format('YYYY-MM-DD')} ${a.schedule_time}`) - new Date(`${moment(b.date).format('YYYY-MM-DD')} ${b.schedule_time}`));

      setTodaysClasses(todayClasses);
    } catch (error) {
      console.error('Error fetching today\'s classes:', error);
    }
  };
  // Move useCallback hooks to the top level of the component
  const fetchTotalMonthlyHours = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/classes/teacher/totalhours/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTotalMonthlyHours(response.data.totalHours);
    } catch (error) {
      console.error('Error fetching total monthly hours:', error);
    }
  }, [id, API_URL]);

  const fetchClassGroups = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/class-groups/teacher/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setClassGroups(response.data);
    } catch (error) {
      console.error('Error fetching class groups:', error);
      setError(t('failedToFetchClassGroups'));
    }
  }, [id, API_URL, t]);

  const fetchRegistrationsCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/registrations/registrations-count`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRegistrationsCount(response.data);
    } catch (error) {
      console.error('Error fetching registrations count:', error);
      setError(t('failedToFetchRegistrationsCount'));
    }
  }, [API_URL, t]);

  useEffect(() => {
    const fetchTeacherData = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/teachers/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        if (response.data) {
          const teacherData = response.data;
          setTeacher({
            ...teacherData,
            bachelorDegree: teacherData.bachelor_degree,
            masterDegree: teacherData.master_degree,
            doctoralDegree: teacherData.doctoral_degree,
            photourl: teacherData.photourl || 'https://via.placeholder.com/150',
          });
        } else {
          setError(t('noTeacherDataFound'));
        }
      } catch (error) {
        console.error('Error fetching teacher data:', error);
        setError(t('failedToFetchTeacherData'));
      } finally {
        setLoading(false);
      }
    };

    fetchTeacherData();
    fetchClassGroups();
    fetchRegistrationsCount();
    fetchTotalMonthlyHours();
    fetchTeacherClasses();
  }, [id, API_URL, t, fetchClassGroups, fetchRegistrationsCount, fetchTotalMonthlyHours]);

  const getRegisteredStudents = (groupId) => {
    const group = registrationsCount.find(rc => rc.groupid === groupId);
    return group ? group.registered_students : 0;
  };

  const today = new Date().setHours(0, 0, 0, 0);

const newClassGroups = classGroups.filter(
  classGroup => new Date(classGroup.start_date) >= today
);
const allClassGroups = classGroups.filter(
  classGroup => new Date(classGroup.start_date) < today
);

// Sort the class groups
const sortedNewClassGroups = [...newClassGroups].sort(
  (a, b) => new Date(a.start_date) - new Date(b.start_date)
);
const sortedAllClassGroups = [...allClassGroups].sort(
  (a, b) => new Date(b.start_date) - new Date(a.start_date)
);

// Update your filtering logic with separate search texts
const filteredNewClassGroups = sortedNewClassGroups.filter(group =>
  group.class_code.toLowerCase().includes(newClassSearchText.toLowerCase())
);

const filteredAllClassGroups = sortedAllClassGroups.filter(group =>
  group.class_code.toLowerCase().includes(allClassSearchText.toLowerCase())
);


  if (loading) return <p>{t('loading')}</p>;
  if (error) return <p>{error}</p>;

  const columns = [
    {
      title: t('classCode'),
      dataIndex: 'class_code',
      key: 'class_code',
      fixed: 'left',
      width: 120,
      render: (class_code, record) => (
        <Link to={`/classdetails/${record.groupid}`}>{class_code}</Link>
      ),
    },
    {
      title: t('subjects'),
      dataIndex: 'subjects',
      key: 'subjects',
      responsive: ['md'],
      render: subjects => (
        <span>{subjects.map(subject => subject.title).join(', ')}</span>
      ),
      ellipsis: false
    },
    {
      title: t('startDate'),
      dataIndex: 'start_date',
      key: 'start_date',
      responsive: ['lg'],
      render: formatDate,
    },
    {
      title: t('endDate'),
      dataIndex: 'end_date',
      key: 'end_date',
      responsive: ['lg'],
      render: formatDate,
    },
    {
      title: t('totalHours'),
      dataIndex: 'total_hours',
      key: 'total_hours',
      responsive: ['md'],
      render: formatScheduleHour,
    },
    {
      title: t('teacherHours'),
      dataIndex: 'teacher_hours',
      key: 'teacher_hours',
      responsive: ['lg'],
    },
    {
      title: t('type'),
      dataIndex: 'isprivate',
      key: 'isprivate',
      render: isprivate => (
        <Tag color={isprivate ? 'blue' : 'green'}>
          {isprivate ? t('private') : t('group')}
        </Tag>
      ),
    },
    {
      title: t('registerCapacity'),
      dataIndex: 'register_capacity',
      key: 'register_capacity',
      responsive: ['md'],
    },
    {
      title: t('registeredStudents'),
      dataIndex: 'groupid',
      key: 'groupid',
      responsive: ['md'],
      render: getRegisteredStudents,
    },
  ];


  return (
    <div className='teacher-dashboard-container'> 
      <Sidebar />
      <Header />
      <div style={{ background: '#f0f2f5', minHeight: '100vh', width: '98%' }}>
        {/* Teacher Header */}
        <Row gutter={[16, 16]}>
          <Col xs={24}>
            <Card>
              <Row justify="space-between" align="middle">
                <Col xs={24} sm={16}>
                  <Space align="start" size={16}>
                    <Avatar size={64} src={teacher.photourl} icon={<UserOutlined />} />
                    <Space direction="vertical" size={0}>
                      <Title level={4} style={{ margin: 0 }}>
                        {teacher.firstname} {teacher.lastname} ({teacher.nickname})
                      </Title>
                      <Text type="secondary">
                        <CalendarOutlined /> {t('dateJoined')}: {formatDate(teacher.createdat)}
                      </Text>
                      <Tag color={teacher.online ? 'success' : 'default'}>
                        {teacher.online ? t('online') : t('offline')}
                      </Tag>
                    </Space>
                  </Space>
                </Col>
                <Col xs={24} sm={8} style={{ textAlign: 'right' }}>
                  <Text type="secondary">
                    {t('lastSignedIn')}: {teacher.last_signed_in ? formatDateTime(teacher.last_signed_in) : 'N/A'}
                  </Text>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} lg={8}>
          <Card 
              title={
                <Space>
                  <CalendarOutlined />
                  <Text strong>{t("Today's Classes")}</Text>
                  <Badge count={todaysClasses.length} />
                </Space>
              }
              bordered={false}
              style={{ height: '700px' }}
              bodyStyle={{ padding: '12px', height: 'calc(100% - 57px)', overflowY: 'auto' }}
            >
              {todaysClasses.length === 0 ? (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  height: '100%',
                  color: '#999'
                }}>
                  <CalendarOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
                  <Text type="secondary">{t('No classes scheduled for today')}</Text>
                </div>
              ) : (
                <List
                  dataSource={todaysClasses}
                  renderItem={(classItem, index) => (
                    <List.Item key={classItem.classid} style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                      <Card 
                        size="small" 
                        style={{ width: '100%', background: '#fafafa' }}
                        bodyStyle={{ padding: '12px' }}
                      >
                        <Row justify="space-between" align="top">
                          <Col span={16}>
                            <Space direction="vertical" size={4}>
                              <Link
                                to={`/classdetails/${classItem.groupid}`}
                                style={{ fontWeight: 'bold', fontSize: '14px' }}
                              >
                                {classItem.class_code}
                              </Link>
                              <Space size={4}>
                                <BookOutlined style={{ fontSize: '12px', color: '#666' }} />
                                <Text style={{ fontSize: '12px', color: '#666' }}>
                                  {classItem.subject_name || 'N/A'}
                                </Text>
                              </Space>
                              <Space size={4}>
                                <TeamOutlined style={{ fontSize: '12px', color: '#666' }} />
                                <Text style={{ fontSize: '12px', color: '#666' }}>
                                  Room: {classItem.classroom_number || 'N/A'}
                                </Text>
                              </Space>
                            </Space>
                          </Col>
                          <Col span={8} style={{ textAlign: 'right' }}>
                            <Space direction="vertical" size={4} align="end">
                              <Tag color="blue" style={{ margin: 0, fontSize: '11px' }}>
                                {classItem.schedule_time && classItem.end_time ? 
                                  `${formatTimeHHMM(classItem.schedule_time)} - ${formatTimeHHMM(classItem.end_time)}` : 
                                  'Time TBD'
                                }
                              </Tag>
                              
                              <Tag color={classItem.isprivate ? 'blue' : 'green'} style={{ margin: 0, fontSize: '10px' }}>
                                {classItem.isprivate ? t('private') : t('group')}
                              </Tag>
                            </Space>
                          </Col>
                        </Row>
                      </Card>
                    </List.Item>
                  )}
                />
              )}
            </Card>
            
          </Col>
          
          <Col xs={24} lg={16}>
          <Card bordered={false}>
          <Row gutter={[24, 24]}>
  {/* Statistics Row */}
  <Col xs={24}>
    <Row gutter={[16, 16]}>
      {/* Main Hours Card */}
      <Col xs={24} md={8}>
        <Card
          style={{
            background: 'linear-gradient(135deg, #1890ff, #096dd9)',
            color: 'white',
            borderRadius: '12px',
            overflow: 'hidden',
          }}
          bodyStyle={{ padding: '24px' }}
        >
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Row justify="space-between" align="middle">
              <Col>
                <Text style={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: '16px' }}>
                  {t('Current Month Hours')}
                </Text>
              </Col>
              <Col>
                <ClockCircleOutlined style={{ fontSize: '24px', color: 'white' }} />
              </Col>
            </Row>
            
            <Statistic
              value={formatMinutesToHours(totalMonthlyHours)}
              valueStyle={{ 
                color: 'white', 
                fontSize: '36px',
                fontWeight: 'bold'
              }}
              prefix={<HourglassOutlined />}
            />

          </Space>
        </Card>
      </Col>

      {/* Average Hours/Week */}
      <Col xs={24} md={8}>
        <Card 
          bordered={false} 
          style={{ 
            background: 'linear-gradient(135deg, #fce3b4, #f9d69e)',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Statistic
            title={t('Average Hours/Week')}
            value={(totalMonthlyHours / (60 * 4)).toFixed(1)}
            suffix={t('hr')}
            prefix={<FieldTimeOutlined style={{ color: '#a17328 ' }} />}
            valueStyle={{ color: '#a17328 ', fontSize: '36px' }}
          />
        </Card>
      </Col>

      {/* Average Hours/Day */}
      <Col xs={24} md={8}>
  <Card 
    bordered={false} 
    style={{ 
      background: 'linear-gradient(135deg, #E8D3EF, #D7BDE2)',// Soft Lavender
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}
  >
    <Statistic
      title={t('Average Hours/Day')} // Updated title
      value={(totalMonthlyHours / (60 * moment().daysInMonth())).toFixed(2)} // Format to two decimal places
      suffix={t('hr')}
      prefix={<FieldTimeOutlined style={{ color: '#6F42C1' }} />} // Soft Violet Icon
      valueStyle={{ color: '#6F42C1', fontSize: '36px' }} // Matching Violet Text
    />
  </Card>
</Col>


    </Row>
  </Col>

  {/* Chart Row - Full Width */}
  <Col xs={24}>
    <Card
      title={
        <Space>
          <BarChartOutlined style={{ fontSize: '20px' }} />
          <span>{t('Monthly Hours Trend')}</span>
        </Space>
      }
      bordered={false}
      headStyle={{ borderBottom: 'none', fontSize: '16px' }}
    >
      <MonthlyHoursTrends teacherId={id} />
    </Card>
  </Col>
</Row>
</Card>
          </Col>
        </Row>
        <div>
      {/* Future Classes Table */}
      <Card style={{ marginTop: 16 }}>
        <h3>{t('upcomingClasses')}</h3>
        <Input.Search
          placeholder="Search upcoming classes by class code"
          value={newClassSearchText}
          onChange={e => setNewClassSearchText(e.target.value)}
          style={{ width: 300, marginBottom: 16 }}
          allowClear
        />
        <Table
          columns={columns}
          dataSource={filteredNewClassGroups}
          rowKey="groupid"
          bordered
          scroll={{ x: 'max-content' }}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
          }}
        />
      </Card>

      {/* Past Classes Table */}
      <Card style={{ marginTop: 16 }}>
        <h3>{t('All Classes')}</h3>
        <Input.Search
          placeholder="Search all classes by class code"
          value={allClassSearchText}
          onChange={e => setAllClassSearchText(e.target.value)}
          style={{ width: 300, marginBottom: 16 }}
          allowClear
        />
        <Table
          columns={columns}
          dataSource={filteredAllClassGroups}
          rowKey="groupid"
          bordered
          scroll={{ x: 'max-content' }}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
          }}
        />
      </Card>
    </div>
      </div>
    </div>
  );
}

export default withRoleAccess(TeacherDashboardAdmin, ['admin', 'superadmin']);